import { formatRublesFromMinorUnits } from 'modules/organizer/domain/utils/money';
import { redirectToExternalUrl } from 'modules/organizer/domain/utils/redirect-to-external-url';
import { WalletTopup } from 'modules/organizer/domain/interfaces/wallet-topup';
import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { checkIsDev } from 'shared/utils/check-is-dev';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { theme } from 'styles/theme/theme';
import { SubscriptionSbpPayTestForm } from './subscription-sbp-pay-test-form';

interface SubscriptionQrStepProps {
  topup: WalletTopup;
  onRetry: () => void;
}

export const SubscriptionQrStep: React.FC<SubscriptionQrStepProps> = ({ topup, onRetry }) => {
  const amountLabel = `${formatRublesFromMinorUnits(topup.amountInMinorUnits)} ₽`;
  const isCard = topup.paymentMethod === 'CARD';

  if (topup.status === 'REFUNDED') {
    return (
      <div className="flex flex-col gap-3">
        <UDText type="headline">Платёж возвращён</UDText>
        <UDText type="body-text" color={theme.colors.Gray}>
          Банк вернул оплату. Создайте новый платёж, если хотите пополнить баланс снова.
        </UDText>
        <UDButton type="button" variant="primary" onClick={onRetry}>
          Попробовать снова
        </UDButton>
      </div>
    );
  }

  if (topup.status === 'REJECTED') {
    return (
      <div className="flex flex-col gap-3">
        <UDText type="headline">Платёж отклонён</UDText>
        <UDText type="body-text" color={theme.colors.Gray}>
          {topup.errorMessage ??
            (isCard
              ? 'Банк отклонил оплату картой. Создайте новый платёж и попробуйте ещё раз.'
              : 'Банк отклонил оплату. Создайте новый QR-код и попробуйте ещё раз.')}
        </UDText>
        <UDButton type="button" variant="primary" onClick={onRetry}>
          Попробовать снова
        </UDButton>
      </div>
    );
  }

  if (topup.status === 'EXPIRED') {
    return (
      <div className="flex flex-col gap-3">
        <UDText type="headline">{isCard ? 'Платёж истёк' : 'QR-код истёк'}</UDText>
        <UDText type="body-text" color={theme.colors.Gray}>
          {isCard
            ? 'Создайте новый платёж, чтобы оплатить картой.'
            : 'Создайте новый платёж, чтобы получить актуальный код.'}
        </UDText>
        <UDButton type="button" variant="primary" onClick={onRetry}>
          {isCard ? 'Создать новый платёж' : 'Создать новый QR'}
        </UDButton>
      </div>
    );
  }

  if (isCard) {
    return (
      <div className="flex flex-col items-start gap-4">
        <UDText type="headline">Оплата картой</UDText>
        <UDText type="body-text" color={theme.colors.Gray}>
          Оплатите картой на странице банка. Сумма: {amountLabel}
        </UDText>
        {topup.paymentUrl && (
          <UDButton type="button" variant="secondary" onClick={() => redirectToExternalUrl(topup.paymentUrl as string)}>
            Открыть страницу банка снова
          </UDButton>
        )}
        <UDText type="footnote" color={theme.colors.Gray}>
          Ожидаем подтверждение оплаты…
        </UDText>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-4">
      <UDText type="headline">Оплата по СБП</UDText>
      <UDText type="body-text" color={theme.colors.Gray}>
        Отсканируйте QR-код в приложении банка. Сумма: {amountLabel}
      </UDText>
      {topup.qrPayload && (
        <div className="rounded-lg border border-darkBlue-100 bg-surfaceWhite p-3">
          <QRCodeSVG value={topup.qrPayload} size={220} />
        </div>
      )}
      {topup.qrPayload && (
        <UDButton type="button" variant="secondary" onClick={() => window.open(topup.qrPayload as string, '_blank')}>
          Открыть в банке
        </UDButton>
      )}
      <UDText type="footnote" color={theme.colors.Gray}>
        Ожидаем подтверждение оплаты…
      </UDText>
      {checkIsDev() && <SubscriptionSbpPayTestForm topupId={topup.id} />}
    </div>
  );
};
